import type { CohortRetentionRow } from '@/features/analytics/analyticsApi';

/**
 * 8-week recipient cohort retention. Each row is a calendar week of first
 * sends; the columns show what fraction of those recipients opened within
 * 1 day / 7 days / 30 days of receiving the email.
 *
 * "Retention" here is open-based, not click-based — opens are a softer signal
 * but cover orders of magnitude more recipients than clicks for transactional
 * traffic, which is where this dashboard mostly lives.
 */
export function CohortRetention({ rows }: { rows: CohortRetentionRow[] }) {
  return (
    <div className="mf-card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 550 }}>Recipient retention · 8 weeks</div>
        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          % opened within window
        </span>
      </div>

      {rows.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--ink-3)',
            fontSize: 12.5,
          }}
        >
          Not enough send history yet — cohorts populate after your first week of sending.
        </div>
      ) : (
        <table className="mf-table" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 110 }}>Cohort week</th>
              <th style={{ width: 90 }}>Recipients</th>
              <th>1 day</th>
              <th>7 days</th>
              <th>30 days</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.week}>
                <td className="mono" style={{ fontSize: 11.5 }}>
                  {r.week}
                </td>
                <td className="mono" style={{ fontSize: 11.5 }}>
                  {r.recipients.toLocaleString()}
                </td>
                <RetentionCell value={r.retention1d} />
                <RetentionCell value={r.retention7d} />
                <RetentionCell value={r.retention30d} />
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RetentionCell({ value }: { value: number }) {
  const pct = value * 100;
  const intensity = Math.min(1, value);
  // Spark green ramps from light to saturated as retention rises. Below 5% we
  // show a near-empty cell so weak cohorts read as weak at a glance.
  const bg = intensity < 0.02 ? 'transparent' : `oklch(0.92 ${0.05 + intensity * 0.18} 148 / ${0.25 + intensity * 0.6})`;
  const color = intensity > 0.45 ? 'oklch(0.30 0.13 148)' : 'var(--ink)';
  return (
    <td style={{ position: 'relative' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 8px',
          borderRadius: 6,
          background: bg,
          color,
          fontSize: 12,
          fontWeight: 500,
          minWidth: 48,
          justifyContent: 'center',
        }}
      >
        {pct < 0.05 ? '—' : `${pct.toFixed(1)}%`}
      </span>
    </td>
  );
}
